import React, { useState } from 'react';

import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Users, FileText, CheckCircle, Clock, XCircle, Mail, Phone, Building, MapPin, Loader2, Shield, CreditCard, Award, RefreshCw, Calendar } from 'lucide-react';
import { formatCodeNAF } from '../../utils/codeNaf';
export { EstimationSimulatorPickerModal } from './MemberEstimationModal';
import axios from 'axios';
import { toast } from 'sonner';
import ProfileCompletenessRadar from '../MembresTable/ProfileCompletenessRadar';
import { EnvoisKlarysSection } from '../klarys/EnvoisKlarysSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function RegenerCertificatSection({ membreId, onRegenerated }) {
  const [showDateInput, setShowDateInput] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const token = localStorage.getItem('token');
      const body = customDate ? { date_adhesion_custom: new Date(customDate).toISOString() } : {};
      await axios.post(`${BACKEND_URL}/api/cooper/membres/${membreId}/regenerer-certificat`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Certificat regenere avec succes');
      setShowDateInput(false);
      setCustomDate('');
      if (onRegenerated) onRegenerated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la regeneration');
    } finally {
      setRegenerating(false);
    }
  };

  if (!membreId) return null;

  return (
    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
      <RefreshCw className="h-4 w-4 text-blue-600 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-sm font-medium text-blue-800">Certificat d&apos;adhesion</p>
        {showDateInput && (
          <div className="flex items-center gap-2 mt-2">
            <Calendar className="h-3.5 w-3.5 text-blue-500" />
            <DatePickerFr value={customDate} onChange={setCustomDate}
              className="h-7 text-xs w-40" id="regen-cert-date" />
          </div>
        )}
      </div>
      <div className="flex gap-1.5">
        <Button variant="outline" size="sm" className="text-xs h-7 border-blue-300 text-blue-700"
          onClick={() => setShowDateInput(!showDateInput)} data-testid="toggle-date-btn">
          <Calendar className="h-3 w-3 mr-1" />{showDateInput ? 'Masquer' : 'Date'}
        </Button>
        <Button size="sm" className="text-xs h-7 bg-blue-600 hover:bg-blue-700"
          onClick={handleRegenerate} disabled={regenerating} data-testid="regen-cert-btn">
          {regenerating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
          Regenerer
        </Button>
      </div>
    </div>
  );
}

const getStatusBadge = (status) => {
  switch (status) {
    case 'actif':
      return <Badge className="bg-green-600 text-white font-semibold"><CheckCircle className="h-3 w-3 mr-1" /> Actif</Badge>;
    case 'en_attente_connexion':
      return <Badge className="bg-orange-500 font-semibold"><Clock className="h-3 w-3 mr-1" /> En attente</Badge>;
    case 'bloque':
      return <Badge className="bg-red-600 text-white font-semibold"><XCircle className="h-3 w-3 mr-1" /> Bloqué</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

// Modal Détail Membre — Vue ultra pro avec toutes les données métier
export function DetailModal({ open, onClose, membre, onCreateSouscription, onEditCoordonnees: onEditCoordonnees }) {
  if (!membre) return null;

  const initials = `${membre.prenom?.[0] || ''}${membre.nom?.[0] || ''}`.toUpperCase() || 'M';
  const fullName = `${membre.prenom || ''} ${membre.nom || ''}`.trim() || membre.entreprise || 'Membre';
  const adresseComplete = [membre.adresse, membre.code_postal, membre.ville].filter(Boolean).join(' · ');
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0 gap-0">
        {/* Header gradient avec avatar XL */}
        <div className="px-6 pt-6 pb-5 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 text-xs font-medium opacity-90">
              <Users className="h-4 w-4" /> Détails du membre
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-start gap-4 mt-3">
            <div className="h-20 w-20 rounded-2xl bg-white/20 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center shadow-lg flex-shrink-0">
              <span className="text-3xl font-bold text-white">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-2xl font-bold text-white leading-tight break-words">{fullName}</h3>
              {membre.numero_membre && (
                <p className="text-sm text-white/80 font-mono mt-1">{membre.numero_membre}</p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {getStatusBadge(membre.status)}
                {membre.souscription_id && (
                  <Badge className="bg-white text-slate-800 text-white font-semibold border-0">
                    <CreditCard className="h-3 w-3 mr-1" /> Souscription active
                  </Badge>
                )}
                {membre.ftpe_actif && (
                  <Badge className="bg-amber-400 text-amber-900 font-semibold border-0">
                    <Award className="h-3 w-3 mr-1" /> FTPE Validé
                  </Badge>
                )}
                {membre.rcds_actif && (
                  <Badge className="bg-emerald-400 text-emerald-900 font-semibold border-0">
                    <Shield className="h-3 w-3 mr-1" /> RCDS Actif
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Score de complétude — radar/spider */}
          <ProfileCompletenessRadar
            membre={membre}
            onCompleteClick={() => {
              onClose(false);
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('open-edit-contact', {
                  detail: { membreId: membre?.id, membre },
                }));
              }, 200);
            }}
          />

          {/* Contact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {membre.email && (
              <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200 hover:border-indigo-200 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-4 w-4 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Email</p>
                  <a href={`mailto:${membre.email}`} className="text-sm font-medium text-gray-900 hover:text-blue-600 break-all">{membre.email}</a>
                </div>
              </div>
            )}
            {membre.telephone && (
              <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200 hover:border-indigo-200 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Téléphone</p>
                  <a href={`tel:${membre.telephone}`} className="text-sm font-medium text-gray-900 hover:text-emerald-600">{membre.telephone}</a>
                </div>
              </div>
            )}
          </div>

          {/* Entreprise */}
          {(membre.entreprise || membre.siret) && (
            <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/30 to-white overflow-hidden">
              <div className="px-4 py-2.5 bg-gradient-to-r from-indigo-50 to-purple-50/40 border-b border-indigo-100 flex items-center gap-2">
                <Building className="h-4 w-4 text-indigo-600" />
                <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider">Entreprise</span>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                {membre.entreprise && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Raison sociale</p>
                    <p className="text-sm font-semibold text-gray-900 break-words">{membre.entreprise}</p>
                  </div>
                )}
                {membre.siret && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">SIRET</p>
                    <p className="text-sm font-mono font-medium text-gray-900">{membre.siret}</p>
                  </div>
                )}
                {membre.statut_juridique && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Statut juridique</p>
                    <p className="text-sm font-medium text-gray-900">{membre.statut_juridique}</p>
                  </div>
                )}
                {membre.code_naf && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Code APE / NAF</p>
                    <p className="text-sm font-mono font-medium text-gray-900">{formatCodeNAF(membre.code_naf)}</p>
                  </div>
                )}
                {membre.date_creation_entreprise && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Date de création</p>
                    <p className="text-sm font-medium text-gray-900">{membre.date_creation_entreprise}</p>
                  </div>
                )}
                {membre.effectif !== undefined && membre.effectif !== null && membre.effectif !== '' && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Effectif</p>
                    <p className="text-sm font-medium text-gray-900">{membre.effectif} salarié(s)</p>
                  </div>
                )}
                {(membre.chiffre_affaires !== undefined && membre.chiffre_affaires !== null && membre.chiffre_affaires !== '') && (
                  <div className="sm:col-span-2">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Chiffre d'affaires</p>
                    <p className="text-base font-bold text-emerald-700">{parseFloat(membre.chiffre_affaires || 0).toLocaleString('fr-FR')} €</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Adresse & Territoire */}
          {(adresseComplete || membre.territoire || membre.pays) && (
            <div className="rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50/30 to-white overflow-hidden">
              <div className="px-4 py-2.5 bg-gradient-to-r from-emerald-50 to-teal-50/40 border-b border-emerald-100 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-emerald-600" />
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Adresse &amp; Territoire</span>
              </div>
              <div className="p-4 space-y-3">
                {membre.adresse && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Adresse</p>
                    <p className="text-sm font-medium text-gray-900">{membre.adresse}</p>
                    {(membre.code_postal || membre.ville) && (
                      <p className="text-sm text-gray-700 mt-0.5">
                        {[membre.code_postal, membre.ville].filter(Boolean).join(' ')}
                        {membre.pays ? ` — ${membre.pays}` : ''}
                      </p>
                    )}
                  </div>
                )}
                {membre.territoire && (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-700 text-white border-emerald-900 font-semibold">
                      Territoire : {membre.territoire}
                    </Badge>
                  </div>
                )}
                {membre.site_web && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Site web</p>
                    <a
                      href={membre.site_web.startsWith('http') ? membre.site_web : `https://${membre.site_web}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-blue-600 hover:underline break-all"
                    >
                      {membre.site_web}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          <EnvoisKlarysSection membreId={membre.id} />

          {/* Métadonnées de création */}
          {(membre.created_at || membre.date_creation || membre.created_by_name) && (
            <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
              {(membre.created_at || membre.date_creation) && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3 w-3 text-gray-400" />
                  Créé le <strong className="text-gray-700">{formatDate(membre.created_at || membre.date_creation) || '-'}</strong>
                </span>
              )}
              {membre.created_by_name && (
                <span className="flex items-center gap-1.5">
                  <Users className="h-3 w-3 text-gray-400" />
                  Par <strong className="text-gray-700">{membre.created_by_name}</strong>
                </span>
              )}
            </div>
          )}

          {/* Actions rapides */}
          {!membre.souscription_id && onCreateSouscription && (
            <div className="border-t pt-4">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Actions rapides</h4>
              <div className="flex flex-wrap gap-2">
                <Button onClick={onCreateSouscription} className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-md gap-2">
                  <CreditCard className="h-4 w-4" /> Créer une souscription
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t bg-gray-50">
          <Button variant="outline" onClick={() => onClose(false)}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Modal Souscription
export function SouscriptionModal({ open, onClose, membre, coopers, selectedCooper, setSelectedCooper, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-purple-600" />Créer une souscription
          </DialogTitle>
          <DialogDescription>
            {membre && <>Pour {membre.prenom} {membre.nom}</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Assigner à un COOPER&apos;S *</Label>
            <Select value={selectedCooper} onValueChange={setSelectedCooper}>
              <SelectTrigger><SelectValue placeholder="Sélectionner un COOPER'S..." /></SelectTrigger>
              <SelectContent>
                {coopers.map((cooper) => (
                  <SelectItem key={cooper.id} value={cooper.id}>
                    {cooper.entreprise || `${cooper.prenom} ${cooper.nom}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">Le COOPER&apos;S sélectionné accompagnera ce membre</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-800">
              <strong>Note :</strong> La souscription sera créée avec le statut &quot;en attente&quot;.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Annuler</Button>
          <Button onClick={onSubmit} className="bg-purple-600 hover:bg-purple-700">
            <CreditCard className="h-4 w-4 mr-2" />Créer la souscription
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



import DocumentSections from '../DocumentSections';

// Modal Documents
export function DocumentsModal({ open, onClose, membre, membreDocuments, loadingDocuments, onRegenerated }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[90vh] overflow-y-auto p-0 gap-0">
        {/* Header gradient pro */}
        <div className="px-6 pt-5 pb-4 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-b border-indigo-100">
          <DialogHeader className="space-y-1">
            <DialogTitle className="flex items-center gap-3 text-base">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md flex-shrink-0">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-gray-900 leading-tight">Documents &amp; Simulations</p>
                <p className="text-xs text-gray-600 mt-0.5 truncate">
                  {membre ? <>Documents de <span className="font-semibold text-gray-800">{membre.prenom} {membre.nom}</span></> : 'Membre'}
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-6 pb-4">
          {loadingDocuments ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : membreDocuments ? (
            <div className="space-y-5 py-4">
              <RegenerCertificatSection membreId={membre?.id} onRegenerated={onRegenerated} />
              <div className="text-xs text-gray-600 bg-gradient-to-br from-slate-50 to-blue-50/50 p-4 rounded-xl border border-blue-100">
                <p className="font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-blue-500" /> Règles d&apos;affichage :
                </p>
                <ul className="list-disc list-inside space-y-0.5 text-gray-600">
                  <li>Simulations en cours : visibles pendant <strong>30 jours</strong></li>
                  <li><strong>Contrats signés</strong> (Attestations RCDS, FTPE, CG/CP) : visibles jusqu&apos;à la <strong>date d&apos;échéance + 1 jour</strong> puis basculent en <strong>« Archivés »</strong></li>
                  <li><strong>Autres documents</strong> (en attente de signature) : Pack 1 Attestation, Pack 2 Conditions, Attestation FTPE non encore signée</li>
                  <li><strong>Archivés</strong> (section dépliable) : contrats échus, accès consultation conservé</li>
                </ul>
              </div>
              <DocumentSections
                membreDocuments={membreDocuments}
                membreId={membre?.id}
                onRefresh={onRegenerated}
                onResume={(simulation) => {
                  // Restaure le bouton "Reprendre" sur les simulations en cours côté Admin (régression V35.x corrigée).
                  // Aligné avec CooperMembresManagement.js (cf. handleReprendreSimulation).
                  const souscriptionId = simulation.souscription_id || simulation.reference || simulation.id;
                  const isDomium = simulation.type === 'simulation_domium' || simulation.source === 'domium_subscriptions';
                  window.location.href = isDomium
                    ? `/simulateur-dos?resume=${souscriptionId}`
                    : `/souscription-annuelle?resume=${souscriptionId}`;
                }}
              />
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Impossible de charger les documents</p>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t bg-gray-50">
          <Button variant="outline" onClick={() => onClose(false)}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Modal FTPE
export function FTPEModal({ open, onClose, membre, ftpeLoading, onAction }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-amber-600" />Action FTPE
          </DialogTitle>
          <DialogDescription>
            {membre && <>Gérer l&apos;adhésion FTPE de {membre.prenom} {membre.nom}</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {membre?.adhesion_ftpe ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <CheckCircle className="h-5 w-5" />Adhésion FTPE Active
                </div>
                {membre.ftpe_expiration_date && (
                  <p className="text-sm text-green-600 mt-1">
                    Expire le {new Date(membre.ftpe_expiration_date).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
              {membre.ftpe_expiration_date &&
               new Date(membre.ftpe_expiration_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                <Button onClick={() => onAction('renouvellement')} disabled={ftpeLoading} className="w-full bg-amber-600 hover:bg-amber-700">
                  {ftpeLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Renouveler l&apos;adhésion FTPE
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-gray-700">Ce membre n&apos;a pas encore d&apos;adhésion FTPE.</p>
                <p className="text-sm text-gray-500 mt-2">L&apos;adhésion FTPE permet de bénéficier de la garantie Fédération TPE.</p>
              </div>
              <Button onClick={() => onAction('adhesion')} disabled={ftpeLoading} className="w-full bg-amber-600 hover:bg-amber-700">
                {ftpeLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
                Demander l&apos;adhésion FTPE
              </Button>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onClose(false)}>Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
